import { useEffect, useRef, useState } from 'react';
import {
  BETA_LOCKOUT_MS,
  BETA_LOCKOUT_STORAGE_KEY,
  BETA_FAIL_COUNT_STORAGE_KEY,
  BETA_MAX_ATTEMPTS,
  clearBetaFailedAttempts,
  getBetaLockoutRemainingMs,
  recordBetaFailedAttempt,
  storeBetaAccessToken,
  subscribeMailchimp,
  verifyBetaCode,
} from '../api/api';
import RepeakedBackdrop from './RepeakedBackdrop';

const C = {
  red: '#E8192C',
  redGlow: 'rgba(232, 25, 44, 0.45)',
  orange: '#FF6B2B',
  bg: '#0C0905',
  text: '#F0F2F5',
  textDim: '#9A938B',
  textVeryDim: '#5C554E',
  card: '#111111',
  border: '#1e1e1e',
  borderBright: '#2A2520',
};

const F = {
  display: '"Bebas Neue", "Oswald", Impact, sans-serif',
  body: '"Rajdhani", "Inter", system-ui, -apple-system, sans-serif',
};

const Diamond = ({ size = 12, color = C.red, hollow = true, style }) => (
  <span
    aria-hidden="true"
    style={{
      display: 'inline-block',
      width: size,
      height: size,
      border: `${Math.max(1.5, size / 9)}px solid ${color}`,
      background: hollow ? 'transparent' : color,
      transform: 'rotate(45deg)',
      boxSizing: 'border-box',
      flexShrink: 0,
      ...style,
    }}
  />
);

const normaliseError = (detail) => {
  if (!detail) return 'Beta key could not be verified';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e.msg || JSON.stringify(e)).join(', ');
  if (typeof detail === 'object') return detail.msg || JSON.stringify(detail);
  return String(detail);
};

const formatLockoutTime = (remainingMs) => {
  const totalSeconds = Math.ceil(remainingMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

export default function BetaGateModal({ open, onApproved, onCancel, siteGate = false }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [lockoutRemainingMs, setLockoutRemainingMs] = useState(() => getBetaLockoutRemainingMs());
  const [mailingEmail, setMailingEmail] = useState('');
  const [mailingStatus, setMailingStatus] = useState('');
  const [mailingSubmitting, setMailingSubmitting] = useState(false);
  const [mailingSuccess, setMailingSuccess] = useState(false);
  const [betaKeyEditable, setBetaKeyEditable] = useState(false);
  const [mailingEditable, setMailingEditable] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !loading && !siteGate) {
        onCancel?.();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [loading, onCancel, open, siteGate]);

  useEffect(() => {
    if (!open) {
      setCode('');
      setError('');
      setLoading(false);
      setMailingEmail('');
      setMailingStatus('');
      setMailingSubmitting(false);
      setMailingSuccess(false);
      setBetaKeyEditable(false);
      setMailingEditable(false);
      return undefined;
    }

    setLockoutRemainingMs(getBetaLockoutRemainingMs());
    if (getBetaLockoutRemainingMs() <= 0) return undefined;

    const interval = setInterval(() => {
      const remaining = getBetaLockoutRemainingMs();
      setLockoutRemainingMs(remaining);
    }, 1000);

    return () => clearInterval(interval);
  }, [open]);

  if (!open) return null;

  const lockedOut = lockoutRemainingMs > 0;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (lockedOut) return;

    const trimmedCode = code.trim();
    if (!trimmedCode) {
      setError('Enter your beta key');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await verifyBetaCode(trimmedCode);
      const betaToken = res.data?.beta_token;
      if (!betaToken) {
        throw new Error('Beta access token was not returned');
      }
      clearBetaFailedAttempts();
      storeBetaAccessToken(betaToken);
      onApproved?.(betaToken);
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) {
        try {
          sessionStorage.setItem(BETA_LOCKOUT_STORAGE_KEY, String(Date.now() + BETA_LOCKOUT_MS));
          sessionStorage.setItem(BETA_FAIL_COUNT_STORAGE_KEY, String(BETA_MAX_ATTEMPTS));
        } catch {
          // Ignore storage errors.
        }
        setLockoutRemainingMs(getBetaLockoutRemainingMs());
        setError('Too many attempts. Try again in a few minutes.');
      } else {
        const attempts = recordBetaFailedAttempt();
        if (attempts >= BETA_MAX_ATTEMPTS) {
          setLockoutRemainingMs(getBetaLockoutRemainingMs());
          setError(`Too many failed attempts. Locked out for ${formatLockoutTime(getBetaLockoutRemainingMs())}.`);
        } else {
          setError(normaliseError(err.response?.data?.detail || err.message));
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMailingSubmit = async (event) => {
    event.preventDefault();
    if (mailingSubmitting || mailingSuccess) return;

    const trimmedEmail = mailingEmail.trim();
    if (!trimmedEmail || !trimmedEmail.includes('@') || !trimmedEmail.includes('.')) {
      setMailingStatus('Please enter a valid email address.');
      return;
    }

    setMailingSubmitting(true);
    setMailingStatus('');
    try {
      await subscribeMailchimp(trimmedEmail);
      setMailingSuccess(true);
      setMailingEmail('');
    } catch (err) {
      setMailingStatus(err.message || 'Something went wrong — try again');
    } finally {
      setMailingSubmitting(false);
    }
  };

  return (
    <RepeakedBackdrop>
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="beta-gate-title"
        onSubmit={handleSubmit}
        autoComplete="off"
        style={{
          width: 'min(440px, 100%)',
          background: `radial-gradient(circle at 85% 10%, rgba(232,25,44,0.14), transparent 45%), rgba(12, 9, 5, 0.92)`,
          border: `1px solid ${C.borderBright}`,
          borderLeft: `3px solid ${C.red}`,
          boxShadow: `0 24px 80px rgba(0,0,0,0.6), 0 0 32px -18px ${C.redGlow}`,
          padding: 28,
          position: 'relative',
          overflow: 'hidden',
          fontFamily: F.body,
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        }}
      >
        <Diamond size={170} color={C.red} style={{ position: 'absolute', right: -72, top: -72, opacity: 0.06 }} />
        <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            fontSize: 11,
            letterSpacing: 3,
            textTransform: 'uppercase',
            color: C.textDim,
            fontWeight: 700,
          }}>
            <Diamond size={8} color={C.orange} hollow={false} />
            Repeaked Beta
          </div>

          <div>
            <h2 id="beta-gate-title" style={{
              fontFamily: F.display,
              color: C.text,
              fontSize: 38,
              lineHeight: 0.95,
              letterSpacing: 1.4,
              margin: 0,
              textTransform: 'uppercase',
            }}>
              Repeaked Beta<span style={{ color: C.red }}>.</span>
            </h2>
            <p style={{
              color: C.textDim,
              fontSize: 14,
              lineHeight: 1.5,
              fontWeight: 600,
              margin: '10px 0 0',
            }}>
              Enter your beta key to access the site.
            </p>
          </div>

          <label htmlFor="repeaked-site-beta-key" style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 7,
            color: C.textDim,
            fontSize: 10,
            letterSpacing: 2.5,
            textTransform: 'uppercase',
            fontWeight: 700,
          }}>
            Beta key
            <input
              ref={inputRef}
              id="repeaked-site-beta-key"
              name="repeaked-site-beta-key"
              type="text"
              value={code}
              onChange={event => setCode(event.target.value)}
              onFocus={() => setBetaKeyEditable(true)}
              readOnly={!betaKeyEditable}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              data-1p-ignore="true"
              data-lpignore="true"
              placeholder="Enter beta key"
              disabled={lockedOut || loading}
              style={{
                width: '100%',
                background: C.card,
                border: `1px solid ${C.border}`,
                borderLeft: `2px solid ${C.red}`,
                color: C.text,
                outline: 'none',
                padding: '12px 14px',
                fontFamily: F.body,
                fontSize: 15,
                fontWeight: 600,
                opacity: lockedOut ? 0.6 : 1,
                WebkitTextSecurity: 'disc',
              }}
            />
          </label>

          {(error || lockedOut) && (
            <div role="alert" style={{
              background: 'rgba(232,25,44,0.08)',
              border: '1px solid rgba(232,25,44,0.25)',
              borderLeft: `3px solid ${C.red}`,
              color: C.text,
              padding: '10px 12px',
              fontSize: 13,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}>
              <Diamond size={7} color={C.red} hollow={false} />
              {lockedOut
                ? `Too many failed attempts. Try again in ${formatLockoutTime(lockoutRemainingMs)}.`
                : error}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: siteGate ? '1fr' : '1fr 1fr', gap: 10 }}>
            {!siteGate && (
              <button
                type="button"
                onClick={onCancel}
                disabled={loading}
                style={{
                  background: 'transparent',
                  border: `1px solid ${C.borderBright}`,
                  color: C.textDim,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontFamily: F.display,
                  fontSize: 13,
                  letterSpacing: 2,
                  padding: '11px 16px',
                  textTransform: 'uppercase',
                }}
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={loading || lockedOut}
              style={{
                background: loading || lockedOut ? '#a4111d' : C.red,
                border: 'none',
                color: C.text,
                cursor: loading || lockedOut ? 'not-allowed' : 'pointer',
                fontFamily: F.display,
                fontSize: 13,
                letterSpacing: 2,
                padding: '11px 16px',
                textTransform: 'uppercase',
                boxShadow: `0 8px 20px -10px ${C.redGlow}`,
              }}
            >
              {loading ? 'Checking...' : 'Unlock site'}
            </button>
          </div>

          <div style={{
            borderTop: `1px solid ${C.border}`,
            paddingTop: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}>
            <p style={{
              color: C.textDim,
              fontSize: 13,
              lineHeight: 1.5,
              fontWeight: 600,
              margin: 0,
            }}>
              Don&apos;t have a beta key? Sign up for updates on our mailing list.
            </p>

            {mailingSuccess ? (
              <p style={{
                fontFamily: F.display,
                fontSize: 14,
                letterSpacing: 2,
                color: C.orange,
                fontWeight: 600,
                margin: 0,
                textTransform: 'uppercase',
              }}>
                You&apos;re on the list — we&apos;ll keep you posted.
              </p>
            ) : (
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  inputMode="email"
                  name="repeaked-newsletter-email"
                  value={mailingEmail}
                  onChange={event => setMailingEmail(event.target.value)}
                  onFocus={() => setMailingEditable(true)}
                  readOnly={!mailingEditable}
                  placeholder="your@email.com"
                  aria-label="Email for mailing list"
                  disabled={mailingSubmitting}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  data-1p-ignore="true"
                  data-lpignore="true"
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      handleMailingSubmit(event);
                    }
                  }}
                  style={{
                    flex: 1,
                    background: C.card,
                    border: `1px solid ${C.border}`,
                    color: C.text,
                    outline: 'none',
                    padding: '10px 12px',
                    fontFamily: F.body,
                    fontSize: 14,
                    fontWeight: 600,
                    opacity: mailingSubmitting ? 0.7 : 1,
                  }}
                />
                <button
                  type="button"
                  onClick={handleMailingSubmit}
                  disabled={mailingSubmitting}
                  style={{
                    background: mailingSubmitting ? '#a4111d' : C.red,
                    border: 'none',
                    color: C.text,
                    cursor: mailingSubmitting ? 'not-allowed' : 'pointer',
                    fontFamily: F.display,
                    fontSize: 12,
                    letterSpacing: 2,
                    padding: '10px 14px',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {mailingSubmitting ? 'Joining...' : 'Notify me'}
                </button>
              </div>
            )}

            {!mailingSuccess && (
              <p style={{
                color: C.textVeryDim,
                fontSize: 11,
                lineHeight: 1.4,
                fontWeight: 600,
                margin: 0,
              }}>
                No spam. Launch updates only.
              </p>
            )}

            {mailingStatus && !mailingSuccess && (
              <p style={{
                color: C.textDim,
                fontSize: 12,
                fontWeight: 600,
                margin: 0,
              }}>
                {mailingStatus}
              </p>
            )}
          </div>

          {siteGate && (
            <div style={{
              color: C.textVeryDim,
              fontSize: 11,
              lineHeight: 1.4,
              fontWeight: 600,
            }}>
              Beta access lasts 7 days on this browser, then you&apos;ll be asked for your key again.
            </div>
          )}
        </div>
      </form>
    </RepeakedBackdrop>
  );
}
